// One function per game endpoint (front-server boot/public/routes.php, Oct 7 2026). Parameter names are the game's own
// (Appendix A of the build plan). Scope: W = world-scoped (wid), V = city-scoped (wid + vid). Sign-in goes through the
// Worker's session routes, never straight to security/*.
import { gameCall, getJson, sessionCall, workerGet, workerPost } from "./http";
import type { TagBook } from "@/game/tagbook";
import type { MarkBook } from "@/game/markbook";
import type { ShareCard, ShareChannel } from "@/game/share";
import type {
  IapProduct, BookmarksResult, GuildDetailsResult, GuildMembersRankingResult, GuildNearbyResult, HelpResult, InitData, MailList, MailThread, MarketSearchResult, NotificationsList, PlayerInfoResult, RallyPrepareResult,
  RankingsResult, ReportBody, ReportsList, SessionInfo, SignInResult, UserTitlesResult, VillageInfoResult, Village, VillageStateResult, WithPrompts, WorldsOverview,
} from "./types";
import { TROOP_KEYS, type BuildingKey, type ResourceKey, type TechKey, type TroopKey } from "@/game/constants";

/** Can this player share to their guild's Discord on a world (Valor Tools src/vweb.js answers). */
export interface DiscordStatus {
  ok?: boolean;
  ready: boolean;
  /** off: Discord isn't set up on Valor Tools · link: no Valor Tools account linked to this game account · guild: no Valor
   *  Tools guild on this world · discord: the guild's Discord isn't linked · setup: this address can't reach Valor Tools */
  reason?: "off" | "link" | "guild" | "discord" | "setup" | string;
  guilds: { id: number; name: string; tag: string | null; dcName: string | null; channels: { key: ShareChannel; label: string; name: string | null }[] }[];
  home?: string;
  error?: string;
  /** Guild Intel (Oct 15 2026): every Valor Tools guild the player is in on this world (Discord or not), or why none */
  intel?: { id: number; name: string; tag: string | null }[];
  intelReason?: "link" | "guild" | string;
}
export interface IntelResult { ok: boolean; guild?: string; tag?: string | null; already?: boolean; error?: string }
export interface DiscordShareResult { ok: boolean; channel?: string; server?: string | null; error?: string }

/** A public report link (worker/shares.ts, Oct 15 2026): made from the game's own copy of the report, kept 7 days. */
export interface ShareLink { ok: boolean; id?: string; url?: string; at?: number; exp?: number; error?: string }
/** What /api/r/<id> answers: the saved report, for anyone. */
export interface SharedReport {
  id: string;
  wid: number;
  wname: string | null;
  /** the subject as the game wrote it */
  s: string;
  /** when it happened, ms */
  ts: number;
  b: ReportBody;
  /** who shared it */
  by: { u: number; l: string | null };
  at: number;
  exp: number;
}
export interface SharedReportResult { ok: boolean; report?: SharedReport; expired?: boolean; error?: string }

/** The Worker's Discord route: its answer as it is, with the player's message on a refusal (not thrown as a game error). */
async function discordCall<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/${path}`, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      headers: body ? { "content-type": "application/json", accept: "application/json" } : { accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    return { ok: false, ready: false, reason: "net", guilds: [], error: "No connection. Check your network and try again." } as T;
  }
  const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!data) return { ok: false, ready: false, reason: "setup", guilds: [], error: `Valor Tools didn't answer (${res.status}). Try again in a moment.` } as T;
  return { guilds: [], ...data, error: (data.error as string) || (res.ok ? undefined : (data.Error as string) || `Request failed (${res.status}).`) } as T;
}

async function shareStop(wid: number, rid: string | number): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch("/api/share/report", { method: "DELETE", credentials: "same-origin", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify({ wid, rid: String(rid) }) });
    const d = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
    return { ok: !!d?.ok, error: d?.error || (res.ok ? undefined : `Request failed (${res.status}).`) };
  } catch {
    return { ok: false, error: "No connection. Check your network and try again." };
  }
}
async function sharedReport(id: string): Promise<SharedReportResult> {
  let res: Response;
  try {
    res = await fetch(`/api/r/${encodeURIComponent(id)}`, { credentials: "omit", headers: { accept: "application/json" } });
  } catch {
    throw new Error("No connection. Check your network and try again.");
  }
  if (res.status === 404) return { ok: false, expired: true };
  const d = (await res.json().catch(() => null)) as SharedReportResult | null;
  if (!res.ok || !d) throw new Error(`The report couldn't be loaded (${res.status}). Try again in a moment.`);
  return d;
}

/** Valor Tools inside Valor Web (Oct 15 2026; worker/vt.ts → Valor Tools src/vweb.js). on: the Admin page switched it on for
 *  the Valor Tools account this game account is linked to. Everything else here is only there when on. */
export interface VtPlan {
  known: boolean;
  paid: boolean;
  hourly: boolean;
  day_until?: number | null;
  trial_until?: number | null;
  sub_until?: number | null;
  sub_all?: boolean;
  addons: { battle: boolean; incoming: boolean };
}
export interface VtMe {
  ok?: boolean;
  on: boolean;
  linked?: boolean;
  name?: string | null;
  login?: string | null;
  home?: string;
  plan?: VtPlan;
  guilds?: { id: number; name: string; tag: string | null; role: string }[];
}
export interface VtClaim { x: number; y: number; city: string; by: string; mine: boolean; until: number | null; tag: string | null; guild: string }
export interface VtOpTarget { x: number; y: number; city: string; op: string; slug: string; land: number; mode: "attack" | "support" | "timed"; tag: string | null }
export interface VtMarks { on: boolean; claims?: VtClaim[]; ops?: VtOpTarget[] }
export interface VtCardWorld { wid: number; name?: string; guild?: string | null; tag?: string | null; ksa?: number; ksd?: number; kst?: number; at?: number }
export interface VtCard {
  name: string;
  slug: string;
  tint?: string;
  motto?: string | null;
  edition?: string | null;
  worlds?: VtCardWorld[];
  titles?: { id: string; name?: string; level?: number }[] | unknown;
}
export interface VtCardResult { on: boolean; card?: VtCard | null; url?: string | null; self?: boolean }
async function vtGet<T>(path: string, off: T): Promise<T> {
  try {
    const res = await fetch(`/api/vt/${path}`, { credentials: "same-origin", headers: { accept: "application/json" } });
    if (!res.ok) return off;
    return ((await res.json().catch(() => null)) as T) || off;
  } catch {
    return off;
  }
}

export type W = { wid: number };
export type V = { wid: number; vid: number };
type Any = WithPrompts & Record<string, unknown>;

export const api = {
  // ---------------------------------------------------------------- the Worker's session
  session: {
    get: () => getJson<SessionInfo>("/api/session"),
    login: (email: string, password: string) => sessionCall<SignInResult>("login", { email, password }),
    register: (email: string, password: string) => sessionCall<SignInResult>("register", { email, password }),
    recover: (email: string) => sessionCall<Any>("recover", { email }),
    logout: () => sessionCall<{ ok: true }>("logout"),
  },

  // ---------------------------------------------------------------- the Worker's city tags (worker/tags.ts)
  tags: {
    get: (wid: number) => workerGet<TagBook>(`tags/${wid}`),
    save: (wid: number, book: TagBook) => workerPost<TagBook>(`tags/${wid}`, book as unknown as Record<string, unknown>),
  },
  // ---------------------------------------------------------------- the Worker's bookmark names and order (worker/marks.ts)
  marks: {
    get: (wid: number) => workerGet<MarkBook>(`marks/${wid}`),
    save: (wid: number, book: MarkBook) => workerPost<MarkBook>(`marks/${wid}`, book as unknown as Record<string, unknown>),
  },

  // ---------------------------------------------------------------- Share to Discord, through Valor Tools' bot (worker/discord.ts)
  discord: {
    status: (wid: number) => discordCall<DiscordStatus>(`discord/${wid}`),
    share: (wid: number, body: { guild: number; channel: ShareChannel; card: ShareCard }) => discordCall<DiscordShareResult>(`discord/${wid}`, body),
  },

  // ---------------------------------------------------------------- Valor Tools Guild Intel (worker/intel.ts)
  intel: {
    add: (wid: number, rid: string | number, guild: number) => discordCall<IntelResult>(`intel/${wid}`, { rid: String(rid), guild }),
  },

  // ---------------------------------------------------------------- Valor Tools inside Valor Web, per player (worker/vt.ts)
  vt: {
    me: (wid: number) => vtGet<VtMe>(`${wid}`, { on: false }),
    marks: (wid: number) => vtGet<VtMarks>(`${wid}/marks`, { on: false }),
    card: (wid: number, name: string) => vtGet<VtCardResult>(`${wid}/card?name=${encodeURIComponent(name)}`, { on: false }),
  },

  // ---------------------------------------------------------------- public report links (worker/shares.ts)
  shares: {
    /** make (or find) the 7-day public link for one of the player's reports */
    create: (wid: number, rid: string | number, wname?: string | null) => discordCall<ShareLink>("share/report", { wid, rid: String(rid), wname: wname ?? null }),
    stop: (wid: number, rid: string | number) => shareStop(wid, rid),
    /** a shared report, for anyone (no sign-in) */
    get: (id: string) => sharedReport(id),
  },

  // ---------------------------------------------------------------- account-wide
  init: (wid?: number) => gameCall<InitData>("init", wid ? { world_id: wid } : {}),
  /** the Terms of Service tree (HelpController) */
  help: () => gameCall<HelpResult>("help"),
  notifications: { list: () => gameCall<NotificationsList>("notifications/list") },
  account: {
    updateLogin: (new_login: string) => gameCall<{ Success?: boolean } & WithPrompts>("account/update_login", { new_login }),
    updateEmail: (email: string) => gameCall<{ Success?: boolean } & WithPrompts>("account/update_email", { email }),
  },
  titles: {
    /** target_uid is required by the server (your own id for your titles); a private other player → privacy 1 and nothing else */
    get: (target_uid: number) => gameCall<UserTitlesResult>("titles/get_user_titles", { target_uid }),
    /** answers {} with a "Title changed" flash */
    setMain: (title: string, tier: number | string) => gameCall<WithPrompts>("titles/set_main", { title, tier }),
    /** the server compares `privacy === 1` strictly — send a number */
    togglePrivacy: (privacy: 0 | 1) => gameCall<WithPrompts>("titles/toggle_privacy", { privacy }),
  },

  // ---------------------------------------------------------------- world-scoped
  world: {
    join: (s: W, direction: string) => gameCall<Any>("world/join", { ...s, direction }),
    startOver: (s: W, direction: string) => gameCall<Any>("world/start_over", { ...s, direction }),
  },
  portal: {
    worlds: (s: W) => gameCall<{ worlds: WorldsOverview } & WithPrompts>("portal/worlds", s),
    charts: (s: W) => gameCall<{ url: string }>("portal/charts", s),
  },
  player: { info: (s: W, target_uid: number) => gameCall<PlayerInfoResult>("player/info", { ...s, target_uid }) },
  villageInfo: (s: W, x: number, y: number) => gameCall<VillageInfoResult>("village/info", { ...s, x, y }),
  rankings: {
    /** The world boards: players (lb_class user) or guilds (lb_class guild), by points / kills (score | ksa | ksd | kst).
     *  20 a page, 1-based; page_num 0 = the page you (or your guild) are on. */
    player: (s: W, p: { lb_class?: "user" | "guild"; lb_info?: "score" | "ksa" | "ksd" | "kst"; page_num?: number }) =>
      gameCall<RankingsResult>("rankings/player", { ...s, lb_class: "user", lb_info: "score", page_num: 0, ...p }),
    /** YOUR guild's members ranked by points (not the world's guilds — that is rankings/player with lb_class guild). */
    guild: (s: W) => gameCall<GuildMembersRankingResult>("rankings/guild", s),
  },
  reports: {
    /** the latest 100; reading the list clears the inbox's new flag */
    list: (s: W) => gameCall<ReportsList>("reports/list", s),
    markRead: (s: W, subject_id: number | string) => gameCall<{ success?: boolean } & WithPrompts>("reports/mark_as_read", { ...s, subject_id }),
    /** soft-deletes */
    delete: (s: W, report_ids: (number | string)[]) => gameCall<{ success?: boolean } & WithPrompts>("reports/delete", { ...s, report_ids }),
  },
  mail: {
    /** every conversation you are part of (reading the list clears village/get's new_mail) */
    list: (s: W) => gameCall<MailList>("mail/list", s),
    /** the letters of one conversation; marks it read */
    view: (s: W, subject_id: number | string) => gameCall<MailThread>("mail/view", { ...s, subject_id }),
    /** "This recipient was not found" for an unknown name; answers { Success: "Mail sent." } */
    send: (s: W, recipient_name: string, subject: string, body: string) => gameCall<WithPrompts>("mail/send", { ...s, recipient_name, subject, body }),
    reply: (s: W, subject_id: number | string, body: string) => gameCall<{ output: { body: string } } & WithPrompts>("mail/reply", { ...s, subject_id, body }),
    delete: (s: W, subject_ids: (number | string)[]) => gameCall<WithPrompts>("mail/delete", { ...s, subject_ids }),
  },
  bookmarks: {
    list: (s: W) => gameCall<BookmarksResult>("bookmarks/list", s),
    /** coords is a list of "x,y" */
    add: (s: W, coords: string[]) => gameCall<BookmarksResult>("bookmarks/add", { ...s, coords }),
    delete: (s: W, coords: string[]) => gameCall<BookmarksResult>("bookmarks/delete", { ...s, coords }),
  },

  // ---------------------------------------------------------------- city-scoped
  village: {
    get: (s: V) => gameCall<Village>("village/get", s),
    expand: (s: V, key: BuildingKey) => gameCall<VillageStateResult>("village/expand", { ...s, key }),
    demolish: (s: V, key: BuildingKey) => gameCall<VillageStateResult>("village/demolish", { ...s, key }),
    cancelExpand: (s: V, assignment_id: number) => gameCall<VillageStateResult>("village/cancel_expand", { ...s, assignment_id }),
    speedupConstruction: (s: V, item_key: string, assignment_id: number) =>
      gameCall<VillageStateResult>("village/speedup_construction", { ...s, item_key, assignment_id }),
    rename: (s: V, name: string) => gameCall<VillageStateResult>("village/rename", { ...s, name }),
  },
  troops: {
    recruit: (s: V, troop_key: TroopKey, amount: number) => gameCall<VillageStateResult>("troops/recruit", { ...s, troop_key, amount }),
    cancelRecruit: (s: V, assignment_id: number, troop_building_key: BuildingKey) =>
      gameCall<VillageStateResult>("troops/cancel_recruit", { ...s, assignment_id, troop_building_key }),
  },
  research: {
    queue: (s: V, key: TechKey) => gameCall<VillageStateResult>("research/queue", { ...s, key }),
    cancel: (s: V, assignment_id: number) => gameCall<VillageStateResult>("research/cancel", { ...s, assignment_id }),
    revoke: (s: V, key: TechKey) => gameCall<VillageStateResult>("research/revoke", { ...s, key }),
  },
  academy: {
    createScholarship: (s: V, quantity: number) => gameCall<VillageStateResult>("academy/create_scholarship", { ...s, quantity }),
    /** purchase_type max_all | max_selected | max_to_buffer (the world reads it as max_to_buffer either way, see docs/api-phase1.md) */
    createMassScholarships: (s: V, purchase_type: "max_all" | "max_selected" | "max_to_buffer", resource_buffer?: Record<ResourceKey, number>) =>
      gameCall<VillageStateResult>("academy/create_mass_scholarships", { ...s, purchase_type, ...(resource_buffer ? { resource_buffer } : {}) }),
    recruitScholar: (s: V) => gameCall<VillageStateResult>("academy/recruit_scholar", s),
    farmAdd: (s: V) => gameCall<VillageStateResult>("academy/farm/add", s),
    farmRemove: (s: V) => gameCall<VillageStateResult>("academy/farm/remove", s),
  },
  rally: {
    /** The target's details + the server's travel time for these units (`city_details["x,y"].target_village.arrival_in`). */
    prepare: (s: V, coords: string, units: TroopKey[]) => gameCall<RallyPrepareResult>("rally/prepare", { ...s, coords, units }),
    /** rally_key "a" attack · "s" support; all nine rally_t_* fields are required (0 allowed). */
    send: (s: V, p: { target_x: number; target_y: number; rally_key: "a" | "s"; units: Partial<Record<TroopKey, number>>; cat_target?: string }) => {
      const body: Record<string, unknown> = { ...s, target_x: p.target_x, target_y: p.target_y, rally_key: p.rally_key, cat_target: p.cat_target ?? "" };
      for (const k of TROOP_KEYS) body[`rally_${k}`] = Math.max(0, Math.floor(p.units[k] ?? 0));
      return gameCall<VillageStateResult>("rally/send", body);
    },
    /** support_type "ss" = withdraw own troops stationed elsewhere (ids index support.sent) · "sr" = send foreign support home (ids index support.received) */
    recall: (s: V, support_ids: number[], support_type: "ss" | "sr") => gameCall<VillageStateResult>("rally/recall", { ...s, support_ids, support_type }),
    cancel: (s: V, assignment_id: number | string) => gameCall<VillageStateResult>("rally/cancel", { ...s, assignment_id }),
    revealCity: (s: V, target_vid: number) => gameCall<VillageStateResult>("rally/reveal_city", { ...s, target_vid }),
    revealIncoming: (s: V, assignmentId: number | string, originCoords: string) =>
      gameCall<VillageStateResult>("rally/reveal_incoming", { ...s, assignmentId, originCoords }),
    simulate: (s: V, p: Record<string, unknown>) => gameCall<Any>("rally/simulate", { ...s, ...p }),
  },
  market: {
    /** answers { Success: true } — no village_state; refetch the city */
    createOffer: (s: V, p: { offer_type: ResourceKey; offer_amount: number; for_type: ResourceKey; for_amount: number; num_offers: number; trade_radius: number }) =>
      gameCall<Any>("market/offers/create", { ...s, ...p }),
    cancelOffer: (s: V, offer_id: number) => gameCall<Any>("market/offers/cancel", { ...s, offer_id }),
    /** radius in HOURS of merchant travel; empty strings mean any resource */
    searchOffers: (s: V, p: { offer_type: ResourceKey | ""; search_type: ResourceKey | ""; radius: number }) =>
      gameCall<MarketSearchResult>("market/offers/search", { ...s, ...p }),
    acceptOffer: (s: V, offer_id: number, num_offers: number, target_village_id: number) =>
      gameCall<VillageStateResult>("market/offers/accept", { ...s, offer_id, num_offers, target_village_id }),
    sendResources: (s: V, p: { target_x: number; target_y: number; r_w: number; r_c: number; r_i: number }) =>
      gameCall<VillageStateResult>("market/send_resources", { ...s, ...p }),
  },
  /** the gold packs the app sells (InAppPurchaseController::products) — shown on the web, not sold */
  iap: { products: () => gameCall<{ products: IapProduct[] }>("iap/products") },
  store: {
    buy: (s: V, item_key: string, amount = 1) => gameCall<VillageStateResult>("store/buy", { ...s, item_key, amount }),
    instantResourceTrade: (s: V, item_key: string, in_resource_key: ResourceKey, out_resource_key: ResourceKey, amount: number) =>
      gameCall<VillageStateResult>("store/instant_resource_trade", { ...s, item_key, in_resource_key, out_resource_key, amount }),
    buyMassConsumables: (s: V, item_key: string, amounts: { amount_wood: number; amount_clay: number; amount_iron: number }) =>
      gameCall<VillageStateResult>("store/buy_mass_consumables", { ...s, item_key, ...amounts }),
    cancelSubscription: (s: V, sub_id: string | number) => gameCall<VillageStateResult>("store/cancel_subscription", { ...s, sub_id }),
  },
  /** spends one amulet; answers { success, spin_result: "0".."15" } — no village_state */
  wheel: { spin: (s: V) => gameCall<{ success: boolean; spin_result: string } & WithPrompts>("wheel/spin", s) },
  /** claims a finished quest's reward (resources/troops into this city, items into the inventory); answers { village_state } */
  quest: { collect: (s: V, quest_key: string) => gameCall<VillageStateResult>("quest/collect", { ...s, quest_key }) },
  /** Every guild action answers { village_state } (the refreshed city, with guild_info). Permission thresholds are the
   *  server's: set_level only for level 0 (Warlord); kick, invites, requests and diplomacy for level ≤ 1. */
  guild: {
    nearby: (s: V) => gameCall<GuildNearbyResult>("guild/nearby", s),
    details: (s: V, gid: number) => gameCall<GuildDetailsResult>("guild/details", { ...s, gid }),
    /** name 1–20 chars · tag 1–6 lowercase letters/digits (the server lowercases) · description 1–200 */
    create: (s: V, name: string, tag: string, description: string) => gameCall<VillageStateResult>("guild/create", { ...s, name, tag, description }),
    editWelcomeMail: (s: V, id: number, subject: string, body: string) => gameCall<VillageStateResult>("guild/edit_welcome_mail", { ...s, id, subject, body }),
    editNews: (s: V, id: number, news: string) => gameCall<VillageStateResult>("guild/edit_news", { ...s, id, news }),
    destroy: (s: V, id: number) => gameCall<VillageStateResult>("guild/destroy", { ...s, id }),
    setLevel: (s: V, user_id: number, level: number) => gameCall<VillageStateResult>("guild/player/set_level", { ...s, user_id, level }),
    leave: (s: V) => gameCall<VillageStateResult>("guild/player/leave", s),
    kick: (s: V, user_id: number) => gameCall<VillageStateResult>("guild/player/kick", { ...s, user_id }),
    /** type a ally · n non-aggression pact · e enemy; `id` is your guild's id, `tag` the other guild's */
    addDiplomacy: (s: V, id: number, tag: string, type: "a" | "n" | "e") => gameCall<VillageStateResult>("guild/diplomacy/add", { ...s, id, tag, type }),
    removeDiplomacy: (s: V, id: number, tag: string) => gameCall<VillageStateResult>("guild/diplomacy/remove", { ...s, id, tag }),
    addInvite: (s: V, gid: number, login: string) => gameCall<VillageStateResult>("guild/invite/add", { ...s, gid, login }),
    removeInvite: (s: V, user_id: number) => gameCall<VillageStateResult>("guild/invite/remove", { ...s, user_id }),
    acceptInvite: (s: V, gid: number) => gameCall<VillageStateResult>("guild/invite/accept", { ...s, gid }),
    declineInvite: (s: V, gid: number) => gameCall<VillageStateResult>("guild/invite/decline", { ...s, gid }),
    /** either gid or gtag identifies the guild ("Guild tag or id is required") */
    addRequest: (s: V, gid: number | null, gtag: string) => gameCall<VillageStateResult>("guild/request/add", { ...s, ...(gid ? { gid } : {}), gtag }),
    removeRequest: (s: V, gid: number) => gameCall<VillageStateResult>("guild/request/remove", { ...s, gid }),
    acceptRequest: (s: V, target_user_id: number, target_login: string) => gameCall<VillageStateResult>("guild/request/accept", { ...s, target_user_id, target_login }),
    declineRequest: (s: V, target_user_id: number, target_login: string) => gameCall<VillageStateResult>("guild/request/decline", { ...s, target_user_id, target_login }),
  },

  // ---------------------------------------------------------------- the world map (Worker-assembled)
  mapUrl: (wid: number, size: number) => `/api/map/${wid}?size=${size}`,
};
